import { AdaptiveSelect } from './AdaptiveSelect';
import type React from 'react';
import { useEffect, useId, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-xl bg-white p-6 shadow-[0_0_20px_rgba(8,21,66,0.05)] ${className}`}>{children}</section>;
}

export function Button({ variant = 'primary', className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' }) {
  const style =
    variant === 'primary'
      ? 'bg-accent text-white shadow-[0_4px_14px_color-mix(in_srgb,var(--color-accent)_25%,transparent)] hover:bg-accent/90 disabled:bg-accent/50 disabled:shadow-none'
      : 'text-ink/70 hover:text-ink hover:bg-black/5 disabled:text-ink/30';
  return <button className={`rounded-[10px] px-4 py-2 text-sm font-medium transition ${style} ${className}`} {...props} />;
}

export function Field({ label, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-ink/80">{label}</span>
      <input className="w-full rounded-[10px] border border-black/[0.08] bg-paper px-3 py-2 outline-none transition-colors focus:border-accent/50 focus:bg-white focus:ring-2 focus:ring-accent/15" {...props} />
    </label>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return children ? (
    <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
      {children}
    </p>
  ) : null;
}

export function Badge({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'amber' | 'green' | 'red' | 'blue' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    amber: 'bg-amber-100 text-amber-800',
    green: 'bg-emerald-100 text-emerald-800',
    red: 'bg-red-100 text-red-800',
    blue: 'bg-blue-100 text-blue-800',
  };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export function Select({ label, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const select = (
    <AdaptiveSelect id={id} aria-label={label} className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-accent" {...props}>
      {children}
    </AdaptiveSelect>
  );
  return label ? (
    <label htmlFor={id} className="block text-sm">
      <span className="mb-1 block font-medium text-ink/80">{label}</span>
      {select}
    </label>
  ) : (
    select
  );
}

export function PageTitle({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end gap-3">
      <div className="min-w-0 flex-1 basis-64">
        <h1 className="text-lg font-semibold">{title}</h1>
        {subtitle && <p className="text-sm text-ink/60">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** Il titolo di una sezione che si apre e si chiude, con quante voci contiene (le aree di ruoli e procedure). */
export function SectionToggle({ title, count, open, onToggle }: { title: string; count: number; open: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" aria-expanded={open} onClick={onToggle}>
      <svg viewBox="0 0 24 24" className={`h-4 w-4 shrink-0 text-ink/40 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="m9 6 6 6-6 6" />
      </svg>
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-xs font-medium text-ink/60">{count}</span>
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-lg border border-dashed border-black/15 px-4 py-6 text-center text-sm text-ink/50">{children}</p>;
}

/** Anello che gira: attesa breve dentro una pagina. */
export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Caricamento"
      className={`inline-block animate-spin rounded-full border-2 border-accent/15 border-t-accent ${className}`}
    />
  );
}

/** Attesa a pagina intera (avvio, cambio azienda): loader al centro dello schermo, compare dopo un attimo per non lampeggiare. */
export function PageLoader() {
  return (
    <div className="bk-fade-in fixed inset-0 flex items-center justify-center bg-paper">
      <Spinner className="h-10 w-10 border-[3px]" />
    </div>
  );
}

/** Attesa dentro una sezione: loader centrato nello spazio del contenuto. */
export function SectionLoader() {
  return (
    <div className="bk-fade-in flex justify-center py-16">
      <Spinner className="h-8 w-8 border-[3px]" />
    </div>
  );
}

/**
 * Menu a tendina sotto un pulsante: si chiude cliccando fuori, con Esc o scegliendo una voce (`close` passato al
 * contenuto). `align` = da che lato si apre.
 */
export function Dropdown({
  trigger,
  children,
  align = 'left',
  label,
}: {
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right';
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <div ref={box} className="relative min-w-0">
      <button type="button" className="flex max-w-full min-w-0 items-center gap-2 rounded-[10px] px-2 py-1.5 text-left hover:bg-accent/[0.06]" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)}>
        {trigger}
        <svg viewBox="0 0 24 24" className={`h-4 w-4 shrink-0 text-ink/40 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div role="menu" className={`absolute top-full z-50 mt-2 w-80 max-w-[calc(100vw-1.5rem)] rounded-xl bg-white p-1.5 shadow-[0_12px_40px_rgba(8,21,66,0.16)] ring-1 ring-black/5 ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

/** Una voce di un menu a tendina. */
export function MenuItem({ children, onClick, active = false, tone }: { children: ReactNode; onClick: () => void; active?: boolean; tone?: 'danger' }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm ${active ? 'bg-accent/10 font-medium text-accent' : tone === 'danger' ? 'text-red-600 hover:bg-red-500/10' : 'text-ink/80 hover:bg-accent/[0.06] hover:text-ink'}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Il segno del prodotto: un anello con la lancetta, il timone ridotto a quadrante (stesso disegno di public/favicon.svg).
 * Prende il colore dal testo: `text-white` su un fondo pieno, `text-accent` sul bianco.
 */
export function AppMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <g fill="none" stroke="currentColor" strokeLinecap="round">
        <circle cx="16" cy="16" r="8.5" strokeWidth="3" />
        <path d="M16 16l4.2-4.2" strokeWidth="2.6" />
      </g>
      <circle cx="16" cy="16" r="2.2" fill="currentColor" />
    </svg>
  );
}
